/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-07-11
 */

import React, { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { ReceiptType, fetchRecords, postFormData } from "../utils/AppUtil";
import { SpinnerLoading } from "../utils/SpinnerLoading";
import { useLocation } from "react-router-dom";

type FormDataType = {
    subscriberEmail: string,
    deceasedId: string,
    donorName: string,
    donorMessage: string,
    anonymousInd: boolean,
    currencyId: string,
    amount: string,
    repeats: string
}

type DonatePostResponse = {
    subscriberId: string,
    subscriberFirstName: string,
    subscriberLastName: string,
    deceasedId: string,
    deceasedFirstName: string,
    deceasedLastName: string,
    donorName: string,
    donorMessage: string,
    approvedInd: boolean,
    anonymousInd: boolean,
    created: string,
    receiptId: string,
    currencyDisplaySymbol: string,
    amount: string,
    repeats: string
  }
    
type CurrencyType = {
    id: string,
    name: string,
    code: string,
    symbol: string,
    position: string,
    decimalPlaces: number,
    deletedInd: boolean
}

type FormProps = {
    endpoint: string;
}

export const DonateForm: React.FC<FormProps> = (props) => {

    const payFormRef = useRef<HTMLFormElement>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [httpError, setHttpError] = useState("");
    const [currency, setCurrency] = useState<CurrencyType>();
    const [payForm, setPayForm] = useState<React.ReactElement[]>();
    const [recurring, setRecurring] = useState(true);

    const [opt1, setOpt1] = useState(true);
    const [opt2, setOpt2] = useState(false);
    const [opt3, setOpt3] = useState(false);
    const [opt4, setOpt4] = useState(false);
    const [opt5, setOpt5] = useState(false);
    const [opt6, setOpt6] = useState(false);

    const [formBody, setFormBody]: any = useState(true);
    const [formMessageSuccess, setFormMessageSuccess]: any = useState(false);
    const [formMessageError, setFormMessageError]: any = useState(false);

    const { search } = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);

    const randAmountsArray = ["180", "540", "1080", "", "", ""];
    const dollarAmountsArray = ["50", "100", "150", "250", "500", "1000"];
    const [optionValues, setOptionValues] = useState<string[]>(randAmountsArray);

    // Do not change this or put any additional code in here without checking with
    // the team first.
    useEffect(() => {
        setCurrency(currency);
        const receiptId: string | null = searchParams.get("receiptId");
        if(receiptId) {
            fetchRecords(`receipts/${receiptId}`, processReceipt)
            .catch((error) => {
                setIsLoading(false);
                setHttpError(error.message);
            });
        } else {
            fetchRecords("currency", processData)
            .catch((error) => {
                setIsLoading(false);
                setHttpError(error.message);
            });
        }

    }, []);

    useEffect(() => {

        if(payFormRef.current){
            payFormRef.current.submit();
        }

    }, [payForm]);

    const formData: FormDataType = {
        subscriberEmail: "",
        deceasedId: "",
        donorName: "",
        donorMessage: "",
        anonymousInd: false,
        currencyId: "",
        amount: "",
        repeats: "0"
    }

    const [formState, setFormState] = useState<FormDataType>(formData);

    const processData = (responseJson: CurrencyType[]) => {
        
        let zar = {} as CurrencyType;
        let usd = {} as CurrencyType;
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        setIsLoading(true);

        for(const record in responseJson) {
            if(responseJson[record].code === "ZAR") {
                zar = responseJson[record];
                setRecurring(true);
            }
            if(responseJson[record].code === "USD") {
                usd = responseJson[record];
                setRecurring(true);
            }            
        }

        if (timeZone === "Africa/Johannesburg") {
            setCurrency(zar);
            formData.currencyId = zar.id;
            formData.amount = Number(randAmountsArray[0]).toFixed(2).toString();
        } else {
            setCurrency(usd);
            formData.currencyId = usd.id;
            formData.amount = Number(dollarAmountsArray[0]).toFixed(2).toString();
            setOptionValues(dollarAmountsArray);
        }
        console.log("currency:", currency);
        setFormState(formData);
        setIsLoading(false);
    }

    const handleMessageSuccessShow = (e?: any) => {
        setFormBody(false);
        setFormMessageSuccess(true);
    };

    const handleMessageErrorShow = (e?: any) => {
        setFormBody(false);
        setFormMessageError(true);
    };

    const handleMessageHide = (e?: any) => {
        setTimeout( ()=> {
            setFormBody(true);
            setFormMessageSuccess(false);
            setFormMessageError(false);
        }, 500)
    };

    const processReceipt = (responseJson: ReceiptType) => {
        if(responseJson.paidInd) {
            handleMessageSuccessShow();
        } else {
            handleMessageErrorShow();
        }
    }
    
    const handlePost = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        const form = {...formState};

        if(form.donorName === "") {
            form.donorName = "Anonymous"; 
        }

        if(form.donorMessage === "") {
            form.donorMessage = "Not Specified"; 
        };

        postFormData(
            props.endpoint,
            form,
            (response: any) => {
                setFormState(formData);
                getPaymentData(response);
            },
            (error: any) => { // error
                console.log("ERROR : " + error);
                setHttpError(error);
            }
        );
    }

    const getPaymentData = (response: DonatePostResponse) => {
        fetchRecords(`receipts/${response.receiptId}/paymentData`, processPaymentData)
        .catch((error) => {
            setIsLoading(false);
            setHttpError(error.message);
        });
    }

    const processPaymentData = (response: any) => {

        const action: string  = response["action"];
        const method: string  = response["method"];

        const data: Map<string, string | null> = new Map<string, string | null>();

        for(const key in response["data"]) {
            const property: any = response["data"][key];
            data.set(property.name, property.value);
        }

        let pfFormInputs: ReactElement[] = [];
        let i: number = 0
        data.forEach((value: string | null, key: string) => {
            if(value != null && value !== "") {
                pfFormInputs.push(
                    <input key={i++} name={key} type="hidden" value={value.trim()} />
                );                
            }
        });
        pfFormInputs.push(
            <input type="submit" value="Proceed to payment ..." style={{visibility: "hidden"}}/>
        );

        let pfForm: ReactElement[] = [];
        pfForm.push(
            <form ref={payFormRef} action={action} method={method} >
                {pfFormInputs}
            </form>
        );

        setPayForm(pfForm);
    }

    const handleFormChange = (e: React.ChangeEvent<any>) => {

        // Doing this to force an update on the recurring amount and the Donate button amount.
        // handleFormChange gets called from the handleAmountButtonClick when the button is clicked.
        if (e.target.type === "button") {
            setFormState((formState) => ({
                ...formState, 
                ["amount"]: formState.amount
            }));
            console.log(formState.amount);
        }

        if (e.target.type === "checkbox") {
            setFormState((formState) => ({
                ...formState, 
                [e.target.id]: e.target.checked
            }));
        }

        if (e.target.type === "text" || e.target.type === "email" || e.target.type === "select-one") {
            let val = e.target.value;
            setFormState((formState) => ({
                ...formState, 
                [e.target.id]: val
            }));
        }

        // console.log("formState : " + JSON.stringify(formState));

    };

    const handleAmountButtonClick = (e: any) => {
        const element: any = document.getElementById("amount");
        element.value = formatAmount(e.target.value);
        formState.amount = formatAmount(e.target.value);
        handleFormChange(e);

        setOpt1(false);
        setOpt2(false);
        setOpt3(false);
        setOpt4(false);
        setOpt5(false);
        setOpt6(false);

        if (e.target.name === "optionValue1") {
            setOpt1(true);
        } else if (e.target.name === "optionValue2") {
            setOpt2(true);
        } else if (e.target.name === "optionValue3") {
            setOpt3(true);
        } else if (e.target.name === "optionValue4") {
            setOpt4(true);
        } else if (e.target.name === "optionValue5") {
            setOpt5(true);
        } else if (e.target.name === "optionValue6") {
            setOpt6(true);
        }

        // console.log(e.target.name);
        // console.log(formState.amount);
    }

    const handleAmountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const element: any = document.getElementById("amount");
        const newAmount = element.value = formatAmount(element.value);

        setFormState((formState) => ({
            ...formState,
            ["amount"]: newAmount
        }))
    }

    const formatAmount = (amount: any) => {
        let result = Number(amount).toFixed(2);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timeZone === "Africa/Johannesburg") {
            if (result === 'NaN' || Number(result) < Number(randAmountsArray[0])){
                result = Number(randAmountsArray[0]).toFixed(2);
            }
        } else {
            if (result === 'NaN' || Number(result) <= Number("10")){
                result = Number("10").toFixed(2);
            }
        }

        return result;
    }

    if(isLoading) {
        return(
            <SpinnerLoading />
        )
    }

    return (
        <>
            {payForm}
            <form id="leafForm" onSubmit={handlePost}>
                <input id="currencydId" name="currencyId" type="hidden" value={currency?.id} onChange={handleFormChange} />

                {formMessageSuccess && 

                    <div className="donate-form-message d-flex flex-column justify-content-center gap-3">
                        <svg width="201" height="200" viewBox="0 0 201 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.5" width="200" height="200" rx="100" fill="#198754"/>
                            <path d="M91.1251 112.919L72.1698 93.9572L72.1696 93.9571C71.3953 93.1827 70.3451 92.7477 69.25 92.7477C68.155 92.7477 67.1048 93.1827 66.3304 93.9571L67.0307 94.6573L66.3304 93.9571C65.5561 94.7314 65.1211 95.7816 65.1211 96.8767C65.1211 97.9717 65.5561 99.022 66.3304 99.7963L67.0281 99.0986L66.3304 99.7963L88.2045 121.67L91.1251 112.919ZM91.1251 112.919L131.955 72.083C131.955 72.0827 131.955 72.0824 131.955 72.0821C132.338 71.6984 132.793 71.3939 133.294 71.1861L133.677 72.1084L133.294 71.1861C133.795 70.9781 134.332 70.8711 134.875 70.8711C135.418 70.8711 135.955 70.9781 136.456 71.1861C136.957 71.3939 137.412 71.6983 137.795 72.0821M91.1251 112.919L137.795 77.9213C138.178 77.5383 138.483 77.0834 138.691 76.5827C138.899 76.0815 139.006 75.5443 139.006 75.0017C139.006 74.4591 138.899 73.9219 138.691 73.4207C138.483 72.92 138.178 72.4651 137.795 72.0821M137.795 72.0821C137.795 72.0824 137.795 72.0827 137.796 72.083L137.088 72.7892L137.794 72.0812C137.794 72.0815 137.794 72.0818 137.795 72.0821ZM94.0455 121.67L137.794 77.9222L94.0455 121.67ZM92.706 122.567C93.2068 122.359 93.6617 122.055 94.0446 121.671H88.2054C88.5884 122.055 89.0433 122.359 89.5441 122.567C90.0452 122.775 90.5825 122.882 91.125 122.882C91.6676 122.882 92.2049 122.775 92.706 122.567Z" fill="white" stroke="white" strokeWidth="2"/>
                        </svg>
                        <p className="jc-txt-body jc-txt-dark fw-6" style={{textAlign: "center"}}>
                            Payment Successful
                        </p>
                        <button onClick={handleMessageHide} type="button" className="btn btn-dark jc-txt-body jc-txt-light" >Close</button>
                    </div>

                }

                {formMessageError && 

                    <div className="donate-form-message d-flex flex-column justify-content-center gap-3">
                        <svg width="201" height="200" viewBox="0 0 201 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.5" width="200" height="200" rx="100" fill="#DC3545"/>
                            <path d="M94.6667 100.002L78.8305 115.832L78.8304 115.832C78.447 116.215 78.1428 116.671 77.9353 117.172L78.8592 117.554L77.9353 117.172C77.7278 117.673 77.621 118.209 77.621 118.752C77.621 119.294 77.7278 119.831 77.9353 120.332L78.8592 119.949L77.9353 120.332C78.1428 120.833 78.447 121.288 78.8304 121.671C79.2138 122.055 79.6689 122.359 80.1699 122.566C80.6708 122.774 81.2078 122.881 81.75 122.881C82.2922 122.881 82.8291 122.774 83.33 122.566C83.831 122.359 84.2862 122.055 84.6696 121.671L84.6697 121.671L100.5 105.835L116.33 121.671L116.33 121.671C116.714 122.055 117.169 122.359 117.67 122.566C118.171 122.774 118.708 122.881 119.25 122.881C119.792 122.881 120.329 122.774 120.83 122.566C121.331 122.359 121.786 122.055 122.17 121.671C122.553 121.288 122.857 120.833 123.065 120.332C123.272 119.831 123.379 119.294 123.379 118.752C123.379 118.209 123.272 117.673 123.065 117.172C122.857 116.671 122.553 116.215 122.17 115.832L122.169 115.832L106.333 100.002L122.169 84.1714L122.17 84.1713C122.553 83.7879 122.857 83.3327 123.065 82.8318C123.272 82.3308 123.379 81.7939 123.379 81.2517C123.379 80.7095 123.272 80.1725 123.065 79.6716C122.857 79.1707 122.553 78.7155 122.17 78.3321C121.786 77.9487 121.331 77.6445 120.83 77.437L120.447 78.3609L120.83 77.437C120.329 77.2295 119.792 77.1227 119.25 77.1227C118.708 77.1227 118.171 77.2295 117.67 77.437L118.053 78.3609L117.67 77.437C117.169 77.6445 116.714 77.9487 116.33 78.3321L116.33 78.3322L100.5 94.1684L84.6705 78.333C84.6703 78.3328 84.6702 78.3327 84.6701 78.3326C84.287 77.9486 83.8319 77.644 83.3309 77.4361L82.9477 78.3597L83.3309 77.4361C82.8298 77.2281 82.2925 77.1211 81.75 77.1211C81.2074 77.1211 80.6701 77.2281 80.169 77.4361C79.6678 77.644 79.2126 77.9488 78.8295 78.333L79.5375 79.0392M94.6667 100.002L79.5375 79.0392M94.6667 100.002L78.8313 84.1722C78.8311 84.172 78.831 84.1719 78.8309 84.1718M94.6667 100.002L78.8309 84.1718M79.5375 79.0392C79.2464 79.3295 79.0156 79.6743 78.858 80.054C78.7005 80.4336 78.6194 80.8406 78.6194 81.2517C78.6194 81.6627 78.7005 82.0697 78.858 82.4494M79.5375 79.0392L78.8313 78.3312C78.4471 78.7144 78.1423 79.1696 77.9344 79.6707C77.7264 80.1719 77.6194 80.7091 77.6194 81.2517C77.6194 81.7943 77.7264 82.3315 77.9344 82.8327L78.858 82.4494M78.858 82.4494L77.9344 82.8327C78.1423 83.3336 78.4469 83.7887 78.8309 84.1718M78.858 82.4494L78.8309 84.1718" fill="white" stroke="white" strokeWidth="2"/>
                        </svg>
                        <p className="jc-txt-body jc-txt-dark fw-6" style={{textAlign: "center"}}>
                            Payment Failed
                        </p>
                        <button onClick={handleMessageHide} type="button" className="btn btn-dark jc-txt-body jc-txt-light" >Try Again</button>
                    </div>

                }

                {formBody && 
                    <div style={{minHeight: "410px"}}>                        
                        <div className="d-flex flex-column gap-1 pb-2">
                            <label htmlFor="donorName" className="jc-txt-body fw-6">Donor's Name (optional)</label>
                            <input id="donorName" name="donorName" type="text" value={formState.donorName} onChange={handleFormChange} className="form-control form-input" placeholder="Your Name" />
                        </div>
                        <div className="d-flex flex-column gap-1 pb-2">
                            <label htmlFor="subscriberEmail" className="jc-txt-body fw-6">Donor's Email (optional)</label>
                            <input id="subscriberEmail" name="subscriberEmail" type="email" value={formState.subscriberEmail} onChange={handleFormChange} className="form-control form-input" placeholder="Your Email" />
                        </div>                        
                        <div className="d-flex flex-column gap-1 pb-2">
                            <label className="jc-txt-body fw-6">In Honor of (optional)</label>
                            <input id="donorMessage" name="donorMessage" type="text" value={formState.donorMessage} onChange={handleFormChange} className="form-control form-input" placeholder="Deceased's Name" />
                        </div>
                        <div className="d-flex flex-column gap-1 pb-2">
                            <label className="jc-txt-body fw-6">Amount</label>
                            <div className="d-flex justify-content-between gap-2">
                                <button name="optionValue1" type="button" className={opt1 ? "btn btn-donate btn-donate-active" : "btn btn-donate btn-secondary"} value={optionValues[0]} onClick={handleAmountButtonClick} >{currency?.symbol}{optionValues[0]}</button>
                                <button name="optionValue2" type="button" className={opt2 ? "btn btn-donate btn-donate-active" : "btn btn-donate btn-secondary"} value={optionValues[1]} onClick={handleAmountButtonClick} >{currency?.symbol}{optionValues[1]}</button>
                                <button name="optionValue3" type="button" className={opt3 ? "btn btn-donate btn-donate-active" : "btn btn-donate btn-secondary"} value={optionValues[2]} onClick={handleAmountButtonClick} >{currency?.symbol}{optionValues[2]}</button>
                            </div>

                            {currency?.code === "USD" ?
                                <div className="d-flex justify-content-between gap-2 mt-2">
                                    <button name="optionValue4" type="button" className={opt4 ? "btn btn-donate btn-donate-active" : "btn btn-donate btn-secondary"} value={optionValues[3]} onClick={handleAmountButtonClick} >{currency?.symbol}{optionValues[3]}</button>
                                    <button name="optionValue5" type="button" className={opt5 ? "btn btn-donate btn-donate-active" : "btn btn-donate btn-secondary"} value={optionValues[4]} onClick={handleAmountButtonClick} >{currency?.symbol}{optionValues[4]}</button>
                                    <button name="optionValue6" type="button" className={opt6 ? "btn btn-donate btn-donate-active" : "btn btn-donate btn-secondary"} value={optionValues[5]} onClick={handleAmountButtonClick} >{currency?.symbol}{optionValues[5]}</button>
                                </div>
                                : ""
                            }
                            <input id="amount" onBlur={handleAmountBlur} name="amount" type="text" value={formState.amount} onChange={handleFormChange} className="form-control form-input my-1" placeholder="or enter an amount" />
                            { recurring &&
                                <select id="repeats" name="repeats" className="form-select form-input my-1" onChange={handleFormChange} >
                                    <option value="0" selected>{currency?.symbol + formatAmount(formState.amount)} once off</option>
                                    <option value="6">{currency?.symbol + formatAmount(formState.amount)} for 6 months</option>
                                    <option value="12">{currency?.symbol + formatAmount(formState.amount)} for 12 months</option>
                                    <option value="18">{currency?.symbol + formatAmount(formState.amount)} for 18 months</option>
                                    <option value="24">{currency?.symbol + formatAmount(formState.amount)} for 24 months</option>
                                </select>
                            }
                        </div>
                        <div className="d-flex justify-content-end mb-4">
                            <button id="donateButton" type="submit" className="btn-submit btn btn-dark jc-txt-body jc-txt-light">Donate {currency?.symbol}{formatAmount(formState.amount)}</button>
                        </div>
                        <div className="d-flex justify-content-center gap-2 pb-4">
                            {currency?.code === "USD" ?
                                <img src="/images/paypal.png" height={24} />
                                :
                                <img src="/images/payfast.png" height={24} />
                            }
                            <img src="/images/mastercard.png" height={24} />
                            <img src="/images/visa.png" height={24} />
                        </div>
                    </div>
                }
            </form>
        </>
    );
}
